import { useEffect, useRef } from 'react';
import { useTracker } from '../../state/store';
import { audioEngine } from '../../engine/audio';
import { Button, Panel } from './primitives';

/**
 * The order list: the arrangement, as a sequence of pattern numbers. Clicking an
 * entry moves the editor there; double-clicking starts playback from it.
 */
export function OrderList() {
  const song = useTracker((s) => s.song);
  const currentOrder = useTracker((s) => s.currentOrder);
  const playback = useTracker((s) => s.playback);
  const revision = useTracker((s) => s.revision);

  const setCurrentOrder = useTracker((s) => s.setCurrentOrder);
  const setOrderEntry = useTracker((s) => s.setOrderEntry);
  const setSongLength = useTracker((s) => s.setSongLength);
  const addPattern = useTracker((s) => s.addPattern);

  const listRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [currentOrder, playback.order]);

  const entries = Array.from({ length: song.songLength }, (_, i) => ({
    index: i,
    pattern: song.order[i] ?? 0,
  }));

  const insertAfter = () => {
    const { order } = song;
    // Shift everything right of the cursor along by one and duplicate the entry.
    for (let i = Math.min(254, song.songLength); i > currentOrder; i--) {
      order[i] = order[i - 1];
    }
    setSongLength(song.songLength + 1);
    setOrderEntry(currentOrder + 1, order[currentOrder]);
    setCurrentOrder(currentOrder + 1);
  };

  const removeEntry = () => {
    if (song.songLength <= 1) return;
    const { order } = song;
    for (let i = currentOrder; i < song.songLength - 1; i++) order[i] = order[i + 1];
    setSongLength(song.songLength - 1);
    setOrderEntry(Math.max(0, song.songLength - 2), order[Math.max(0, song.songLength - 2)]);
    setCurrentOrder(Math.min(currentOrder, song.songLength - 2));
  };

  const newPattern = () => {
    const index = addPattern();
    setOrderEntry(currentOrder, index);
  };

  return (
    <Panel
      title="Order"
      dense
      actions={
        <span className="font-mono text-[10px] text-ink-500">
          {song.songLength} pos · {song.patterns.length} pat
        </span>
      }
    >
      <div className="flex h-full flex-col">
        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-1.5 py-1.5" key={revision}>
          {entries.map(({ index, pattern }) => {
            const isCursor = index === currentOrder;
            const isPlaying = playback.playing && index === playback.order;
            return (
              <button
                key={index}
                ref={isCursor ? activeRef : undefined}
                type="button"
                onClick={() => setCurrentOrder(index)}
                onDoubleClick={() => {
                  audioEngine.seek(index, 0);
                  audioEngine.play();
                }}
                className={`flex w-full items-center justify-between rounded px-2 py-[3px] text-left font-mono text-[11px] transition-colors ${
                  isCursor
                    ? 'bg-acid-900 text-acid-300 ring-1 ring-acid-600'
                    : isPlaying
                      ? 'bg-ink-750 text-white'
                      : 'text-ink-400 hover:bg-ink-800 hover:text-white'
                }`}
              >
                <span className="text-ink-500">{String(index).padStart(3, '0')}</span>
                <span className={isPlaying ? 'text-acid-400' : ''}>
                  {String(pattern).padStart(2, '0')}
                </span>
              </button>
            );
          })}
        </div>

        <div className="shrink-0 border-t border-ink-700 p-2">
          <div className="mb-2 flex items-center gap-1">
            <Button
              onClick={() => setOrderEntry(currentOrder, Math.max(0, (song.order[currentOrder] ?? 0) - 1))}
              className="flex-1"
              title="Point this position at the previous pattern"
            >
              −
            </Button>
            <Button
              onClick={() => setOrderEntry(currentOrder, (song.order[currentOrder] ?? 0) + 1)}
              className="flex-1"
              title="Point this position at the next pattern"
            >
              +
            </Button>
          </div>
          <div className="flex items-center gap-1">
            <Button onClick={insertAfter} className="flex-1" title="Insert a position after this one">
              Ins
            </Button>
            <Button onClick={removeEntry} className="flex-1" title="Delete this position">
              Del
            </Button>
          </div>
          <Button onClick={newPattern} className="mt-1 w-full" title="Create a new empty pattern here">
            New pattern
          </Button>
        </div>
      </div>
    </Panel>
  );
}
